import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Context } from "../../context/Context";
import { Button, CircleX, Select } from "../ui";
import { useModalLayer, useSubmitGuard } from "./modalHooks";
import "./RejectModal.css";

export default function RejectModal({
  open,
  loading = false,
  onConfirm,
  onClose,
  type = "default",
  stages = [],
  defaultStage = "",
  // Optional message from the caller, e.g. when onConfirm's request failed.
  submitError = "",
}) {
  const { lang } = useContext(Context);
  const isStageReject = type === "stage";

  const [reason, setReason] = useState("");
  const [selectedStage, setSelectedStage] = useState(defaultStage ? String(defaultStage) : "");
  const [error, setError] = useState("");
  const titleId = useId();
  const dialogRef = useRef(null);
  // Guards callers that don't pass `loading`; while busy the modal can't be
  // dismissed or re-submitted (Escape, backdrop, Ctrl+Enter, buttons).
  const [guardBusy, runConfirm] = useSubmitGuard();
  const busy = loading || guardBusy;
  const requestClose = () => {
    if (!busy) onClose?.();
  };

  const TEXT = {
    title: lang === "ar" ? "الرجاء إدخال سبب الرفض" : "Please enter rejection reason",
    placeholder: lang === "ar" ? "اكتب سبب الرفض..." : "Write rejection reason...",
    stageLabel: lang === "ar" ? "المرحلة" : "Stage",
    stagePlaceholder: lang === "ar" ? "اختر المرحلة" : "Select stage",
    confirm: lang === "ar" ? "تأكيد" : "Confirm",
    cancel: lang === "ar" ? "إلغاء" : "Cancel",
    loading: lang === "ar" ? "جاري التحميل..." : "Loading...",
    reasonRequired: lang === "ar" ? "الرجاء إدخال سبب الرفض" : "Please enter rejection reason",
    stageRequired: lang === "ar" ? "الرجاء اختيار المرحلة" : "Please select a stage",
  };

  const stageOptions = useMemo(() => {
    return stages.map((stage) => {
      if (typeof stage === "object") {
        return {
          value: String(stage.value),
          label: stage.label,
        };
      }

      return {
        value: String(stage),
        label: lang === "ar" ? `المرحلة ${stage}` : `Stage ${stage}`,
      };
    });
  }, [lang, stages]);

  useEffect(() => {
    if (open) {
      setSelectedStage(defaultStage ? String(defaultStage) : "");
      return;
    }

    setReason("");
    setSelectedStage(defaultStage ? String(defaultStage) : "");
    setError("");
  }, [defaultStage, open]);

  const handleConfirm = async () => {
    if (isStageReject && !selectedStage) {
      setError(TEXT.stageRequired);
      return;
    }

    if (!reason.trim()) {
      setError(TEXT.reasonRequired);
      return;
    }

    setError("");

    if (isStageReject) {
      await runConfirm(onConfirm, reason.trim(), Number(selectedStage));
      return;
    }

    await runConfirm(onConfirm, reason.trim());
  };

  useModalLayer(open, { containerRef: dialogRef, onEscape: requestClose });

  // Ctrl/Cmd+Enter submits (not while a submission is running).
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && !busy) handleConfirm();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (!open) return null;

  return (
    <div className="reject-modal-backdrop" onClick={requestClose}>
      <div
        ref={dialogRef}
        className="reject-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reject-modal-header">
          <h3 id={titleId}>{TEXT.title}</h3>

          <Button
            type="button"
            variant="danger"
            appearance="soft"
            size="sm"
            rounded
            icon={<CircleX size={18} />}
            onClick={requestClose}
            disabled={busy}
            aria-label={TEXT.cancel}
            title={TEXT.cancel}
          />
        </div>

        <div className="reject-modal-body">
          {isStageReject && (
            <Select
              label={TEXT.stageLabel}
              value={selectedStage}
              placeholder={TEXT.stagePlaceholder}
              options={stageOptions}
              disabled={busy}
              fullWidth
              onChange={(event) => {
                setSelectedStage(event.target.value);
                if (error) setError("");
              }}
            />
          )}

          <textarea
            autoFocus={!isStageReject}
            value={reason}
            placeholder={TEXT.placeholder}
            aria-label={TEXT.title}
            disabled={busy}
            onChange={(event) => {
              setReason(event.target.value);
              if (error) setError("");
            }}
          />

          {(error || submitError) && (
            <p className="reject-error" role="alert">{error || submitError}</p>
          )}
        </div>

        <div className="reject-modal-actions">
          <Button
            type="button"
            variant="primary"
            appearance="filled"
            onClick={handleConfirm}
            disabled={busy}
            fullWidth
          >
            {busy ? TEXT.loading : TEXT.confirm}
          </Button>

          <Button
            type="button"
            variant="danger"
            appearance="soft"
            onClick={requestClose}
            disabled={busy}
            fullWidth
          >
            {TEXT.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
